import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Calendar, DollarSign, CreditCard, Target, Percent, TrendingUp, Package, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmtBRL = (v) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v, d = 2) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

const MiniCard = ({ label, value, accent = 'text-white' }) => (
  <div className="bg-gray-800 rounded-xl p-4">
    <span className="text-gray-400 text-xs">{label}</span>
    <div className={`text-xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);

const AfterPaySection = ({ period, start, end }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    let url = `/afterpay/metrics?period=${period || 'last_7d'}`;
    if (period && period.startsWith('custom') && start && end) url += `&start=${start}&end=${end}`;
    api.get(url)
      .then((res) => { if (alive && res.data && res.data.success) setData(res.data.metrics); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, start, end]);

  const m = data || {};
  const hasData = (m.agendamentos || 0) > 0;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-cyan-700/30 overflow-hidden">
      {/* Header da sessao */}
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400"><Calendar size={20} /></div>
          <div className="text-left">
            <h2 className="text-lg font-bold text-white">After Pay</h2>
            <p className="text-gray-400 text-sm">Pedidos agendados vs realizados</p>
          </div>
        </div>
        {open ? <ChevronUp className="text-gray-400" size={20} /> : <ChevronDown className="text-gray-400" size={20} />}
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-4">
          {loading ? (
            <div className="text-gray-400 text-sm py-4">Carregando métricas...</div>
          ) : !hasData ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
              <AlertTriangle size={16} className="text-yellow-400" />
              Nenhum pedido agendado no período selecionado.
            </div>
          ) : (
            <>
              {/* Cards principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 rounded-xl p-5 border border-cyan-700/30">
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-400 text-sm">Faturamento Agendado</span>
                    <Calendar className="text-cyan-400" size={20} />
                  </div>
                  <div className="mt-2"><span className="text-2xl font-bold text-white">{fmtBRL(m.faturamentoAgendado)}</span></div>
                  <div className="text-xs text-gray-400 mt-1">{m.agendamentos || 0} agendamentos</div>
                </div>
                <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-5 border border-green-700/30">
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 text-sm">Faturamento Realizado</span>
                    <DollarSign className="text-green-400" size={20} />
                  </div>
                  <div className="mt-2"><span className="text-2xl font-bold text-white">{fmtBRL(m.faturamentoRealizado)}</span></div>
                  <div className="text-xs text-gray-400 mt-1">{m.pagos || 0} pagos ({fmtNum(m.pctPagamento, 1)}%)</div>
                </div>
                <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-5 border border-blue-700/30">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-400 text-sm">ROAS Agendamento</span>
                    <Target className="text-blue-400" size={20} />
                  </div>
                  <div className="mt-2"><span className="text-2xl font-bold text-white">{fmtNum(m.roasAgendamento)}x</span></div>
                  <div className="text-xs text-gray-400 mt-1">Realizado: {fmtNum(m.roasRealizado)}x</div>
                </div>
                <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 rounded-xl p-5 border border-red-700/30">
                  <div className="flex items-center justify-between">
                    <span className="text-red-400 text-sm">Frustração</span>
                    <Percent className="text-red-400" size={20} />
                  </div>
                  <div className="mt-2"><span className="text-2xl font-bold text-white">{fmtNum(m.pctFrustracao, 1)}%</span></div>
                  <div className="text-xs text-gray-400 mt-1">{m.frustrados || 0} frustrados</div>
                </div>
              </div>

              {/* Cards secundarios */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <MiniCard label="CPA Agendamento" value={fmtBRL(m.cpaAgendamento)} accent="text-yellow-400" />
                <MiniCard label="CPA Realizado" value={fmtBRL(m.cpaRealizado)} accent="text-yellow-400" />
                <MiniCard label="ROI Agendamento" value={fmtNum(m.roiAgendamento) + 'x'} />
                <MiniCard label="ROI Realizado" value={fmtNum(m.roiRealizado) + 'x'} />
                <MiniCard label="Ticket Médio Agend." value={fmtBRL(m.ticketMedioAgendamento)} />
                <MiniCard label="CPL" value={m.totalLeads > 0 ? fmtBRL(m.cpl) : '—'} />
                <MiniCard label="% Sucesso Entrega" value={m.enviados > 0 ? fmtNum(m.pctSucessoEntrega, 1) + '%' : '—'} accent="text-green-400" />
                <MiniCard label="% Inadimplência" value={m.entregues > 0 ? fmtNum(m.pctInadimplencia, 1) + '%' : '—'} accent="text-red-400" />
                <MiniCard label="Tempo Médio Envio" value={m.tempoMedioEnvio !== null && m.tempoMedioEnvio !== undefined ? fmtNum(m.tempoMedioEnvio, 1) + 'h' : '—'} />
                <MiniCard label="Tempo Médio Entrega" value={m.tempoMedioEntrega !== null && m.tempoMedioEntrega !== undefined ? fmtNum(m.tempoMedioEntrega, 1) + 'h' : '—'} />
              </div>

              {/* Top Produtos + grafico */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Package size={16} className="text-cyan-400" />
                    <span className="text-white text-sm font-semibold">Top Produtos Agendados</span>
                  </div>
                  {(m.topProdutos || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-700/50 last:border-0">
                      <span className="text-gray-300 text-sm truncate">{i + 1}. {p.productName || 'Sem nome'}</span>
                      <span className="text-gray-400 text-xs whitespace-nowrap ml-2">{p.agendamentos}x · {fmtBRL(parseFloat(p.valorAgendado))}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={16} className="text-cyan-400" />
                    <span className="text-white text-sm font-semibold">Agendado vs Realizado</span>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={[{ name: 'Período', Agendado: m.faturamentoAgendado || 0, Realizado: m.faturamentoRealizado || 0 }]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} />
                      <YAxis stroke="#9CA3AF" fontSize={12} />
                      <Tooltip contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8 }} formatter={(v) => fmtBRL(v)} />
                      <Bar dataKey="Agendado" fill="#22D3EE" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Realizado" fill="#4ADE80" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {m.enviados === 0 && (
                <div className="text-xs text-gray-500">
                  Métricas de entrega aparecem quando os webhooks da 123logs começarem a chegar.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AfterPaySection;
